import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useLayoutEffect, useState } from 'react'

import { useEventListener } from './useEventListener'
import { useLatest } from './useLatest'

/**
 * Хук для связи React с localStorage
 *
 * @param key ключ для localStorage
 * @param convertFromLocalStorage функция десериализации строки в желаемый тип
 * @param convertToLocalStorage функция сериализация желаемого типа в строку
 * @param initialValue значение по умолчанию
 * @returns аналогично useState
 */
export const useLocalStorage = <T>(
  key: string,
  convertFromLocalStorage: (item: string | null) => T,
  convertToLocalStorage: (item: T) => string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>] => {
  const readValue = useCallback(() => {
    try {
      const fromLocalStorage = window.localStorage.getItem(key)
      return fromLocalStorage ? convertFromLocalStorage(fromLocalStorage) : initialValue
    } catch (error) {
      console.error(error)
      return initialValue
    }
  }, [convertFromLocalStorage, initialValue, key])

  const [storedValue, setStoredValue] = useState<T>(readValue)
  const storedLatest = useLatest(storedValue)

  const setItem = useCallback<Dispatch<SetStateAction<T>>>(
    value => {
      const oldValue = storedLatest.current
      const newValue = value instanceof Function ? value(oldValue) : value

      window.localStorage.setItem(key, convertToLocalStorage(newValue))
      setStoredValue(newValue)

      window.dispatchEvent(
        new StorageEvent('local-storage', {
          key,
          newValue: convertToLocalStorage(newValue),
          oldValue: convertToLocalStorage(oldValue),
        }),
      )
    },
    [convertToLocalStorage, key],
  )

  useLayoutEffect(() => {
    setStoredValue(readValue())
  }, [readValue])

  const handleLocalStorageChange = useCallback(
    (event: StorageEvent) => {
      if (event.key !== key) return

      setStoredValue(readValue())
    },
    [key, readValue],
  )

  useEventListener('storage', handleLocalStorageChange)
  useEventListener('local-storage', handleLocalStorageChange)

  return [storedValue, setItem]
}

export {}

declare global {
  interface WindowEventHandlersEventMap {
    ['local-storage']: StorageEvent
  }
}
